import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertCircle, Building2, CalendarClock, RefreshCw, ShieldCheck, X } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar } from "@/components/avatar";
import { AvatarPicker } from "@/components/avatar-picker";
import {
  DetailProperty,
  FormPanel,
  Mono,
  type ResourceConfig,
  ResourcePage,
  type RowAction,
} from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { api, errorMessage } from "@/lib/api";
import type { Translate, TranslationKey } from "@/lib/i18n";
import { profileQuery } from "@/lib/queries";
import { useTranslation } from "@/stores/locale";

export const Route = createFileRoute("/_app/employees")({ component: EmployeesPage });

/** The panel footer's Save button reaches the form by name; only one is open. */
const FORM_ID = "employee-form";

/** An employee is a user. */
interface Employee {
  id: number;
  user_name: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  full_name: string;
  avatar?: string | null;
  email_address: string | null;
  status: "active" | "password_expired" | "account_locked" | "inactive" | "deleted";
  is_superuser: boolean;
  is_password_request: boolean;
  club_id: number | null;
  manager_id: number | null;
  club: { id: number; club_name: string; club_code?: string } | null;
  is_temporary_club: boolean;
  temporary_club: { id: number; club_name: string; club_code?: string } | null;
  temporary_club_reason: string | null;
  temporary_start_date: string | null;
  temporary_end_date: string | null;
  has_active_temporary_club: boolean;
  groups: { id: number; name: string }[];
  request_reason?: string | null;
  password_request_at?: string | null;
  force_password_change?: boolean;
  last_password_change_at?: string | null;
  last_login_at?: string | null;
  created_by_id: number | null;
  updated_by_id: number | null;
  created_at: string;
  updated_at: string;
}

/** Roles that get an email address; everyone else reports to a manager instead. */
const EMAIL_ROLES = new Set(["Supervisor", "Manager / Corporate Admin", "Super User"]);

const STATUS: Record<
  Employee["status"],
  { label: TranslationKey; tone: "success" | "danger" | "neutral" }
> = {
  active: { label: "emp.statusActive", tone: "success" },
  account_locked: { label: "emp.statusLocked", tone: "danger" },
  password_expired: { label: "emp.statusExpired", tone: "danger" },
  inactive: { label: "emp.statusInactive", tone: "neutral" },
  deleted: { label: "emp.statusDeleted", tone: "neutral" },
};

const role = (t: Translate, row: Employee) =>
  row.is_superuser ? t("shell.roleSuperuser") : row.groups.map((g) => g.name).join(", ") || "—";

/** The home club, and under it any posting on record with its window. */
function ClubCell({ row, showPosting }: { row: Employee; showPosting: boolean }) {
  const t = useTranslation();
  const day = (value: string | null) => (value ? format(new Date(value), "MM/dd/yyyy") : "…");
  return (
    <span className="block min-w-0">
      <span className="text-fg block">{row.club?.club_name ?? "—"}</span>
      {showPosting && row.is_temporary_club && row.temporary_club ? (
        <span
          className="text-fg-muted text-footnote mt-0.5 flex flex-wrap items-center gap-x-1.5"
          title={row.temporary_club_reason ?? undefined}
        >
          <CalendarClock className="size-3 shrink-0" aria-hidden />
          <span className="text-fg">{row.temporary_club.club_name}</span>
          <span className="whitespace-nowrap">
            {day(row.temporary_start_date)} – {day(row.temporary_end_date)}
          </span>
          {row.has_active_temporary_club ? (
            <Badge tone="elevated">{t("emp.statusActive")}</Badge>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

const config = (
  t: Translate,
  openRequest: (row: Employee) => void,
): ResourceConfig<Employee> => ({
  title: t("nav.employees"),
  singular: t("log.employee"),
  endpoint: "/users",
  model: "user",
  // Newest first, off the primary key — the same default the members list and
  // the geography screens take.
  defaultSort: { key: "id", order: "desc" },
  // The box is two searches at once: every word typed is matched as a fragment
  // of the three name parts, in any order, while the text as a whole is
  // compared to the Employee ID and the email address. Half an Employee ID
  // finds nobody on purpose — it is an identifier, not a name — so the
  // placeholder says "full" rather than leaving people to discover it.
  searchPlaceholder: t("emp.search"),
  // Three characters before the box asks, as on Members. Below that a trigram
  // index has no whole trigram to look up, so the query degrades to a scan for
  // an answer of thousands of rows nobody wanted.
  searchMinLength: 3,
  columns: [
    // The id opens the record, as it does on every other list.
    {
      key: "id",
      label: t("rp.id"),
      sortKey: "id",
      render: (row) => (
        <span className="text-accent underline underline-offset-4">{row.id}</span>
      ),
    },
    {
      key: "user_name",
      label: t("emp.employeeNumber"),
      sortKey: "user_name",
      render: (row) => <Mono>{row.user_name}</Mono>,
    },
    // The email sits under the name and the posting under the club, as they
    // always have. What is new is that each has its own switch: turned off in
    // Customize Columns, the second line simply is not drawn.
    {
      key: "full_name",
      label: t("emp.name"),
      sortKey: "name",
      fields: ["full_name", "avatar", "user_name", "email_address"],
      render: (row, _lookup, visible) => (
        <span className="inline-flex items-center gap-2.5">
          <Avatar
            src={row.avatar ?? null}
            name={row.full_name || row.user_name}
            className="text-footnote size-7"
          />
          <span className="min-w-0">
            <span className="text-fg block font-medium">{row.full_name || "—"}</span>
            {row.email_address && visible("email_address") ? (
              <span className="text-fg-muted text-footnote block truncate">
                {row.email_address}
              </span>
            ) : null}
          </span>
        </span>
      ),
    },
    { key: "email_address", label: t("emp.emailAddress"), inline: true, render: () => null },
    {
      key: "club",
      label: t("emp.club"),
      fields: ["club", "temporary_club", "temporary_start_date", "temporary_end_date"],
      render: (row, _lookup, visible) => (
        <ClubCell row={row} showPosting={visible("temporary_club")} />
      ),
    },
    { key: "temporary_club", label: t("emp.temporaryClub"), inline: true, render: () => null },
    { key: "role", label: t("emp.role"), render: (row) => role(t, row) },
    {
      key: "manager",
      label: t("emp.manager"),
      render: (row, lookup) => (row.manager_id ? lookup("manager_id", row.manager_id) : "—"),
    },
    {
      key: "status",
      label: t("emp.status"),
      render: (row) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone={STATUS[row.status].tone}>{t(STATUS[row.status].label)}</Badge>
          {row.is_password_request ? (
            <Badge tone="elevated">{t("emp.passwordRequest")}</Badge>
          ) : null}
        </span>
      ),
    },
  ],
  filters: [
    { name: "club_id", label: t("emp.club"), type: "fk", endpoint: "/clubs/compact" },
    { name: "group_id", label: t("emp.role"), type: "fk", endpoint: "/groups/compact" },
    { name: "created_from", label: t("geo.createdFrom"), type: "date" },
    { name: "created_to", label: t("geo.createdTo"), type: "date" },
    {
      name: "status",
      label: t("emp.status"),
      type: "select",
      options: Object.entries(STATUS).map(([value, { label }]) => ({ value, label: t(label) })),
    },
    {
      name: "temporary_club_id",
      label: t("emp.temporaryClubFilter"),
      type: "fk",
      endpoint: "/clubs/compact",
    },
  ],
  fields: [],
  toForm: () => ({}),
  toolbar: (
    <Button asChild variant="secondary">
      <Link to="/employee-activity-log">{t("page.activityLog")}</Link>
    </Button>
  ),
  renderForm: (editing, onClose, onSaved) => (
    <EmployeeForm
      employee={editing === "new" ? null : editing}
      onClose={onClose}
      onSaved={onSaved}
    />
  ),
  // A superuser short-circuits every permission check, so deleting one locks
  // everybody out of what only it can reach — and editing someone else's is an
  // escalation path: change the address, then ask for a reset to it. The API
  // refuses both; these stop offering the buttons.
  rowCanDelete: (row) => !row.is_superuser,
  rowCanEdit: (row, profile) => !row.is_superuser || profile?.id === row.id,
  // Read in the order someone asks about an account: who they are, whether it
  // works, then the password trail. Created and updated stay in the footnote
  // after all of it — a record's own history reads last.
  detailSections: [
    // The picture leads, the way it does on a profile — it was under every
    // field in a slab of its own at the bottom.
    { title: t("emp.sectionPicture"), render: (row) => <EmployeePicture row={row} /> },
    { title: t("emp.sectionIdentity"), fields: ["user_name", "email_address"] },
    { title: t("emp.sectionAccess"), fields: ["status", "is_superuser", "manager_id"] },
    // Its own section, straight after Access, rather than an afterthought
    // below the whole record: where someone works is not a footnote.
    { title: t("emp.sectionClub"), render: (row) => <EmployeeClub row={row} /> },
    {
      title: t("emp.sectionPassword"),
      fields: [
        "force_password_change",
        "is_password_request",
        "request_reason",
        "password_request_at",
        "last_password_change_at",
        "last_login_at",
      ],
    },
  ],
  detailLabels: {
    user_name: t("emp.employeeNumber"),
    email_address: t("emp.emailAddress"),
    status: t("emp.status"),
    is_superuser: t("emp.isSuperuser"),
    manager_id: t("emp.manager"),
    force_password_change: t("emp.forcePasswordChange"),
    is_password_request: t("emp.isPasswordRequest"),
    request_reason: t("emp.requestReason"),
    password_request_at: t("emp.requestedAt"),
    last_password_change_at: t("emp.lastPasswordChange"),
    last_login_at: t("emp.lastLogin"),
  },
  detailHide: [
    "avatar",
    // Null on most accounts, so it survives the "no objects" rule and shows as
    // an empty row; the Club section below says it properly when there is one.
    "temporary_club",
    "club",
    // The Club section below says all of this, with the club's name rather
    // than its id and the window the posting applies over.
    "club_id",
    "temporary_club_id",
    "effective_club_id",
    // `is_active` is `status` read a second way.
    "is_active",
    "is_temporary_club",
    "has_active_temporary_club",
    "temporary_club_reason",
    "temporary_start_date",
    "temporary_end_date",
  ],
  rowActions: (row, refresh) => rowActions(t, row, refresh, openRequest),
});

function EmployeesPage() {
  const t = useTranslation();
  const [request, setRequest] = useState<Employee | null>(null);
  const queryClient = useQueryClient();
  const cfg = useMemo(() => config(t, setRequest), [t]);
  return (
    <>
      <ResourcePage config={cfg} />
      {request ? (
        <PasswordRequestDialog
          row={request}
          onClose={() => setRequest(null)}
          onDone={() => void queryClient.invalidateQueries({ queryKey: ["/users"] })}
        />
      ) : null}
    </>
  );
}

/** One PATCH per menu action; the edit endpoint is the only way an account changes. */
const patch = (t: Translate, row: Employee, body: object, done: string, refresh: () => void) =>
  api
    .patch(`/users/${row.id}`, body)
    .then(() => {
      toast.success(done);
      refresh();
    })
    .catch((err: unknown) => toast.error(errorMessage(err, t("emp.updateFailed"))));

function rowActions(
  t: Translate,
  row: Employee,
  refresh: () => void,
  openRequest: (row: Employee) => void,
): RowAction[] {
  const actions: RowAction[] = [];
  if (row.is_password_request)
    actions.push({ label: t("emp.passwordRequestAction"), run: () => openRequest(row) });
  actions.push(
    row.force_password_change
      ? {
          label: t("emp.stopRequiring"),
          run: () =>
            void patch(
              t,
              row,
              { force_password_change: false },
              t("emp.noLongerRequired"),
              refresh,
            ),
        }
      : {
          label: t("emp.requireNew"),
          run: () =>
            void patch(t, row, { force_password_change: true }, t("emp.requiredNext"), refresh),
        },
  );
  if (row.status !== "deleted" && !row.is_superuser)
    actions.push(
      row.status === "active"
        ? {
            label: t("emp.deactivate"),
            run: () =>
              void patch(t, row, { status: "inactive" }, t("emp.deactivated"), refresh),
            danger: true,
          }
        : {
            label: t("emp.activate"),
            run: () => void patch(t, row, { status: "active" }, t("emp.activated"), refresh),
          },
    );
  return actions;
}

/** The request as the person made it — reason and when — then the decision. */
function PasswordRequestDialog({
  row,
  onClose,
  onDone,
}: {
  row: Employee;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useTranslation();
  const ref = useDialog();
  const act = useMutation({
    mutationFn: (verb: "approve" | "reject") =>
      api.post(`/users/${row.id}/password-request/${verb}`),
    onSuccess: (_, verb) => {
      toast.success(verb === "approve" ? t("emp.sentToManager") : t("emp.requestRejected"));
      onDone();
      ref.current?.close();
    },
    onError: (err) => toast.error(errorMessage(err, t("emp.requestFailed"))),
  });
  return (
    <dialog ref={ref} onClose={onClose} className={dialogClass}>
      <div className="border-line flex items-start justify-between gap-4 border-b pb-4">
        <div>
          <h2 className="text-title-3">{t("emp.passwordRequest")}</h2>
          <p className="text-fg-muted text-callout mt-1">
            {row.full_name || row.user_name} · {row.user_name}
          </p>
        </div>
        <button
          type="button"
          onClick={() => ref.current?.close()}
          className="text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-8 shrink-0 place-items-center rounded-lg transition-colors"
        >
          <X className="size-4.5" aria-hidden />
          <span className="sr-only">{t("common.close")}</span>
        </button>
      </div>
      <dl className="text-callout mt-4 grid gap-3">
        <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-4">
          <dt className="text-fg-muted">{t("emp.reason")}</dt>
          <dd className="text-fg break-words">{row.request_reason ?? "—"}</dd>
        </div>
        <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-4">
          <dt className="text-fg-muted">{t("emp.requested")}</dt>
          <dd className="text-fg">
            {row.password_request_at
              ? format(new Date(row.password_request_at), "MMM d, yyyy, h:mm a")
              : "—"}
          </dd>
        </div>
      </dl>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="outline" disabled={act.isPending} onClick={() => act.mutate("reject")}>
          {t("emp.reject")}
        </Button>
        <Button disabled={act.isPending} onClick={() => act.mutate("approve")}>
          {t("emp.approve")}
        </Button>
      </div>
    </dialog>
  );
}

// --- generate a password the API will accept: 14 chars, every class present ---
function generatePassword(): string {
  const sets = ["abcdefghjkmnpqrstuvwxyz", "ABCDEFGHJKLMNPQRSTUVWXYZ", "23456789", "!@#$%*?"];
  const pick = (chars: string) =>
    chars[crypto.getRandomValues(new Uint32Array(1))[0]! % chars.length]!;
  const all = sets.join("");
  const chars = [...sets.map(pick), ...Array.from({ length: 10 }, () => pick(all))];
  for (let i = chars.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0]! % (i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join("");
}

function useDialog() {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);
  return ref;
}

const dialogClass =
  "bg-surface-raised text-fg m-auto w-[calc(100%-2rem)] max-w-2xl rounded-sheet animate-sheet p-6 shadow-[var(--shadow-sheet)]";

/** The asterisk convention, kept in one place so every form marks the same way. */
function Required({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <span aria-hidden className="text-danger ml-0.5">
        *
      </span>
    </>
  );
}

function EmployeeForm({
  employee,
  onClose,
  onSaved,
}: {
  employee: Employee | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  const creating = employee === null;

  const [values, setValues] = useState({
    user_name: employee?.user_name ?? "",
    first_name: employee?.first_name ?? "",
    middle_name: employee?.middle_name ?? "",
    last_name: employee?.last_name ?? "",
    group_id: employee?.groups[0]?.id.toString() ?? "",
    club_id: employee?.club_id?.toString() ?? "",
    manager_id: employee?.manager_id?.toString() ?? "",
    email_address: employee?.email_address ?? "",
    password: "",
  });
  const [posting, setPosting] = useState(employee?.is_temporary_club ?? false);
  const [temp, setTemp] = useState({
    temporary_club_id: employee?.temporary_club?.id.toString() ?? "",
    temporary_club_reason: employee?.temporary_club_reason ?? "",
    temporary_start_date: employee?.temporary_start_date ?? "",
    temporary_end_date: employee?.temporary_end_date ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const set = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }));
  const [roleName, setRoleName] = useState(employee?.groups[0]?.name ?? "");
  const showEmail = EMAIL_ROLES.has(roleName);

  // Creating: the number is the key into SNR. It refuses a number that already
  // has an account, and otherwise fills in what HR knows. Until it has, the
  // rest of the form stays locked — the number decides what the form holds.
  const [lookup, setLookup] = useState({
    note: null as string | null,
    busy: false,
    found: false,
    failed: false,
  });
  // The form opens up once SNR has answered — or once it could not: an outage
  // at SNR must not stop an account being created by hand.
  const editable = !creating || lookup.found || lookup.failed;
  const lookupSnr = async () => {
    const number = values.user_name.trim();
    if (!creating || !number || lookup.found) return;
    setLookup({ note: null, busy: true, found: false, failed: false });
    setError(null);
    try {
      const found = (
        await api.get<{
          first_name: string | null;
          middle_name: string | null;
          last_name: string | null;
          email_address: string | null;
          position: string | null;
          role: string | null;
          role_id: number | null;
        }>(`/snr/${encodeURIComponent(number)}`)
      ).data;
      setValues((v) => ({
        ...v,
        first_name: found.first_name ?? v.first_name,
        middle_name: found.middle_name ?? v.middle_name,
        last_name: found.last_name ?? v.last_name,
        email_address: found.email_address ?? v.email_address,
        group_id: found.role_id ? String(found.role_id) : v.group_id,
      }));
      setLookup({
        busy: false,
        found: true,
        failed: false,
        note: found.role_id
          ? t("emp.foundRole")
              .replace("{position}", found.position ?? "—")
              .replace("{role}", found.role ?? "")
          : t("emp.foundNoRole").replace("{position}", found.position ?? "—"),
      });
    } catch (err) {
      setLookup({
        busy: false,
        found: false,
        failed: true,
        note: `${errorMessage(err, t("emp.snrFailed"))} ${t("emp.fillByHand")}`,
      });
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        first_name: values.first_name,
        middle_name: values.middle_name || null,
        last_name: values.last_name,
        email_address: showEmail ? values.email_address || null : null,
        manager_id: !showEmail && values.manager_id ? Number(values.manager_id) : null,
        club_id: values.club_id ? Number(values.club_id) : null,
        group_ids: values.group_id ? [Number(values.group_id)] : [],
      };
      const saved = creating
        ? (
            await api.post<Employee>("/users", {
              ...body,
              user_name: values.user_name,
              password: values.password,
              force_password_change: true,
            })
          ).data
        : (
            await api.patch<Employee>(`/users/${employee.id}`, {
              ...body,
              // The posting rides along: set it, end it, or leave it untouched.
              ...(posting
                ? {
                    temporary_club: {
                      temporary_club_id: Number(temp.temporary_club_id),
                      temporary_club_reason: temp.temporary_club_reason,
                      temporary_start_date: temp.temporary_start_date,
                      temporary_end_date: temp.temporary_end_date,
                    },
                  }
                : employee.is_temporary_club
                  ? { temporary_club: null }
                  : {}),
            })
          ).data;
      return saved;
    },
    onSuccess: () => {
      toast.success(creating ? t("emp.created") : t("emp.updated"));
      onSaved();
    },
    onError: (err) => setError(errorMessage(err, t("emp.saveFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };

  return (
    <FormPanel
      title={creating ? t("emp.create") : t("emp.edit")}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          {/* The fields sit in a `<fieldset disabled>` until SNR has answered,
              and a disabled control is neither validated nor submitted — so a
              Save outside that fieldset would post a form of empty strings
              with nothing to stop it. It waits for the same signal they do. */}
          <Button
            type="submit"
            form={FORM_ID}
            disabled={!editable}
            loading={save.isPending}
            loadingLabel={t("new.saving")}
          >
            {t("rp.save")}
          </Button>
        </>
      }
    >
      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout mb-4 flex items-start gap-2 rounded-lg px-3.5 py-2.5"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}

      <form id={FORM_ID} onSubmit={submit} className="grid gap-4 md:grid-cols-2 md:items-start">
        <Field
          label={<Required>{t("emp.employeeNumber")}</Required>}
          htmlFor="user_name"
          {...(lookup.busy || lookup.note
            ? { hint: lookup.busy ? t("emp.lookingUp") : lookup.note! }
            : {})}
        >
          <Input
            id="user_name"
            required
            disabled={!creating}
            inputMode="numeric"
            pattern="[0-9]+"
            value={values.user_name}
            onChange={(e) => {
              set("user_name", e.target.value.replace(/\D/g, ""));
              setLookup({ note: null, busy: false, found: false, failed: false });
            }}
            onBlur={() => void lookupSnr()}
            placeholder={t("emp.enterEmployeeNumber")}
          />
        </Field>
        <fieldset disabled={!editable} className="contents">
          <Field label={<Required>{t("emp.firstName")}</Required>} htmlFor="first_name">
            <Input
              id="first_name"
              required
              value={values.first_name}
              onChange={(e) => set("first_name", e.target.value)}
              placeholder={t("emp.enterName")}
            />
          </Field>
          <Field label={t("emp.middleName")} htmlFor="middle_name">
            <Input
              id="middle_name"
              value={values.middle_name}
              onChange={(e) => set("middle_name", e.target.value)}
            />
          </Field>
          <Field label={<Required>{t("emp.lastName")}</Required>} htmlFor="last_name">
            <Input
              id="last_name"
              required
              value={values.last_name}
              onChange={(e) => set("last_name", e.target.value)}
            />
          </Field>
          <Field label={<Required>{t("emp.role")}</Required>} htmlFor="group_id">
            <FkSelect
              id="group_id"
              required
              endpoint="/groups/compact"
              value={values.group_id}
              onChange={(next, label) => {
                set("group_id", next);
                setRoleName(label);
              }}
              placeholder={t("emp.selectRole")}
            />
          </Field>
          <Field label={<Required>{t("emp.club")}</Required>} htmlFor="club_id">
            <FkSelect
              id="club_id"
              required
              endpoint="/clubs/compact"
              value={values.club_id}
              onChange={(next) => set("club_id", next)}
              placeholder={t("emp.selectClub")}
            />
          </Field>

          {creating ? (
            <Field
              label={
                <span className="inline-flex items-center gap-2">
                  <Required>{t("emp.temporaryPassword")}</Required>
                  <button
                    type="button"
                    onClick={() => set("password", generatePassword())}
                    className="border-line bg-surface-inset text-fg text-footnote inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-medium"
                  >
                    <RefreshCw className="size-3" aria-hidden /> {t("emp.generate")}
                  </button>
                </span>
              }
              htmlFor="password"
              hint={t("emp.passwordHint")}
            >
              <Input
                id="password"
                required
                readOnly
                value={values.password}
                placeholder={t("emp.clickToGenerate")}
                className="bg-surface-inset font-mono"
                onClick={() => !values.password && set("password", generatePassword())}
              />
            </Field>
          ) : null}

          {/* Which of the two a person gets follows their role, so neither shows until one is chosen. */}
          {roleName === "" ? null : showEmail ? (
            <Field label={<Required>{t("emp.emailAddress")}</Required>} htmlFor="email_address">
              <Input
                id="email_address"
                type="email"
                required
                value={values.email_address}
                onChange={(e) => set("email_address", e.target.value)}
              />
            </Field>
          ) : (
            <Field label={t("emp.manager")} htmlFor="manager_id">
              <FkSelect
                id="manager_id"
                endpoint="/users/compact"
                value={values.manager_id}
                onChange={(next) => set("manager_id", next)}
                exclude={(option) => option.id === employee?.id}
                placeholder={t("emp.selectManager")}
              />
            </Field>
          )}

          {creating ? null : (
            <label className="text-callout flex items-start gap-3 pt-7 md:col-start-2">
              <input
                type="checkbox"
                className="accent-accent mt-0.5 size-5"
                checked={posting}
                onChange={(e) => setPosting(e.target.checked)}
              />
              <span className="text-fg font-semibold">{t("emp.addTemporaryClub")}</span>
            </label>
          )}

          {posting ? (
            <>
              <Field
                label={<Required>{t("emp.temporaryClub")}</Required>}
                htmlFor="temporary_club_id"
              >
                <FkSelect
                  id="temporary_club_id"
                  required
                  endpoint="/clubs/compact"
                  value={temp.temporary_club_id}
                  onChange={(next) => setTemp((t) => ({ ...t, temporary_club_id: next }))}
                  exclude={(option) => String(option.id) === values.club_id}
                  placeholder={t("emp.selectClub")}
                />
              </Field>
              <Field
                label={<Required>{t("emp.reason")}</Required>}
                htmlFor="temporary_club_reason"
              >
                <Input
                  id="temporary_club_reason"
                  required
                  value={temp.temporary_club_reason}
                  onChange={(e) =>
                    setTemp((t) => ({ ...t, temporary_club_reason: e.target.value }))
                  }
                  placeholder={t("emp.whyPosting")}
                />
              </Field>
              <Field
                label={<Required>{t("emp.startDate")}</Required>}
                htmlFor="temporary_start_date"
              >
                <Input
                  id="temporary_start_date"
                  type="date"
                  required
                  value={temp.temporary_start_date}
                  onChange={(e) =>
                    setTemp((t) => ({ ...t, temporary_start_date: e.target.value }))
                  }
                />
              </Field>
              <Field
                label={<Required>{t("emp.endDate")}</Required>}
                htmlFor="temporary_end_date"
              >
                <Input
                  id="temporary_end_date"
                  type="date"
                  required
                  min={temp.temporary_start_date || undefined}
                  value={temp.temporary_end_date}
                  onChange={(e) =>
                    setTemp((t) => ({ ...t, temporary_end_date: e.target.value }))
                  }
                />
              </Field>
            </>
          ) : null}
        </fieldset>
      </form>
    </FormPanel>
  );
}

/** The profile picture, and the two ways to change one. */
function EmployeePicture({ row }: { row: Employee }) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const { data: profile } = useQuery(profileQuery());
  // The panel holds the row it opened with, so the new picture is kept here to
  // show at once; the list is invalidated for everywhere else.
  const [picture, setPicture] = useState<string | null | undefined>(undefined);
  const avatar = picture === undefined ? row.avatar : picture;
  const save = useMutation({
    // Its own endpoint: an ordinary edit should not carry a couple of hundred
    // kilobytes of picture on a path that has nothing to do with one.
    mutationFn: (value: string) => api.put(`/users/${row.id}/avatar`, { avatar: value }),
    onSuccess: (_, value) => {
      setPicture(value || null);
      toast.success(value ? t("emp.pictureUpdated") : t("emp.pictureRemoved"));
      void queryClient.invalidateQueries({ queryKey: ["/users"] });
      if (profile?.id === row.id) void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error) => toast.error(errorMessage(error, t("emp.pictureFailed"))),
  });

  return (
    <AvatarPicker
      src={avatar}
      name={row.full_name || row.user_name}
      busy={save.isPending}
      // Same rule as the record itself: a superuser's account is theirs alone.
      editable={!row.is_superuser || profile?.id === row.id}
      onChange={(dataUrl) => save.mutate(dataUrl)}
      onRemove={() => save.mutate("")}
    />
  );
}

/** Where they work: the role they hold, the club, and any posting over it. */
function EmployeeClub({ row }: { row: Employee }) {
  const t = useTranslation();
  return (
    <dl className="grid gap-x-12 md:grid-cols-2">
      <DetailProperty label={t("emp.role")} icon={ShieldCheck}>
        {role(t, row)}
      </DetailProperty>
      <DetailProperty label={t("emp.club")} icon={Building2}>
        <ClubCell row={row} showPosting />
      </DetailProperty>
      {row.is_password_request ? (
        <DetailProperty label={t("emp.passwordRequest")}>
          {row.request_reason ?? "—"}
        </DetailProperty>
      ) : null}
    </dl>
  );
}
